import { CSSProperties, FC, useState } from "react";

import { AutoComplete } from "antd";
import checkMark from "assets/images/checkmark.svg";
import warning from "assets/images/warning.svg";
import SuffixIcon from "components/inputs/Form/Suffix/Suffix";
import { useLocalStorage } from "hooks/helpers/useLocalStorage";
import { FiPlus } from "react-icons/fi";
import { PiMapPinFill } from "react-icons/pi";
import { GoogleSessionToken } from "types/client";
import { emboldenOverlappingText } from "utils/embolden-overlapping-text";
import { generateSessionToken, getPlaceDetails, placesAutocomplete } from "utils/google-autocomplete";

export type FormDataState = {
    [key: string]: any;
};

type AutoCompleteProps = {
    placeholder?: string;
    error?: string;
    index?: number;
    value?: string;
    style?: CSSProperties;
    suffix?: string;
    onChange: (_newValue: string, _object?: {[key: string]: string}) => void;
    handleKeyDown?: (_event: any) => void;
    nonUKCountryEnabled?: boolean;
}

type Option = {
    value: string;
    label: string;
}

type Address = {
    address1: string;
    address2: string;
    city: string;
    country: string;
    postcode: string;
}

interface AutoCompleteItemProps extends Option {
    inputValue: string;
    index: number;
}

const AutoCompleteItem = ({ inputValue, value, label, index }: AutoCompleteItemProps) => {
    if (index === 0) {
        return ({
            value,
            label: (<span style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <FiPlus style={{ marginRight: "2px", minWidth: "20px" }} size={20} fill='grey'/>
                <span>{label}</span>
            </span>),
        });
    }
    return ({
        value,
        label: (<span className='fs13' style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <PiMapPinFill style={{ marginRight: "2px", minWidth: "20px" }} size={20} fill='grey'/>
            {emboldenOverlappingText(label, inputValue)}
        </span>),
    });
};

const AutoCompleteAddressInput: FC<AutoCompleteProps> = ({
    value,
    index,
    placeholder,
    error,
    suffix,
    onChange,
    handleKeyDown,
    nonUKCountryEnabled = false,
}) => {
    const emptyAddressObject: Address = {
        "address1": "",
        "address2": "",
        "city": "",
        "country": "",
        "postcode": "",
    };

    const manualToggleOption = {
        value: "manual",
        label: "Enter address manually",
    };

    const [options, setOptions] = useState<Option[]>([]);
    const [storedTokenData, setTokenData] = useLocalStorage<GoogleSessionToken| null>("sessionTokenData", null);

    const handleSelect = async (optionKey: string) => {
        if (optionKey === manualToggleOption.value) {
            onChange("", emptyAddressObject);
            return;
        }

        const rawAddressOption = options.find(option => option.value === optionKey);
        const formattedAddress = await getPlaceDetails(optionKey, true); // <-- now awaits a Promise

        if (rawAddressOption?.label && formattedAddress) {
            onChange(rawAddressOption.label, formattedAddress);
        } else {
            onChange("", emptyAddressObject);
        }

        setTokenData(null);
    };

    const getSessionToken = async () => {
        let sessionToken;
        const currentTime = new Date().getTime();
        if (storedTokenData && (currentTime - storedTokenData.timestamp) < 3 * 60 * 1000) {
            sessionToken = storedTokenData.token;
        } else {
            sessionToken = generateSessionToken();
            setTokenData({ token: sessionToken, timestamp: currentTime });
        }
        return sessionToken;
    };

    const handleChange = async (newValue?: string) => {
        if (newValue === manualToggleOption.value) return;
        onChange(newValue || "");
        if (!newValue) {
            setOptions([]);
            return;
        }

        const autoComplete = await placesAutocomplete(newValue, await getSessionToken(), nonUKCountryEnabled ? [] : ["gb", "ie"]);
        setOptions(autoComplete || []);
    };

    return (
        <AutoComplete
            placeholder={placeholder}
            size={"large"}
            value={value}
            data-testid={"autocompleteAddressInput"}
            onSearch={handleChange}
            onSelect={handleSelect}
            options={[manualToggleOption, ...options].map((option, index) => AutoCompleteItem({ inputValue: value || "", index, ...option }))}
            autoFocus={index === 0}
            onKeyDown={handleKeyDown}
            suffixIcon={
                <SuffixIcon
                    hasError={!!error}
                    hasValue={!!value}
                    suffixSrc={suffix}
                    checkmarkSrc={checkMark}
                    warningSrc={warning}
                />
            }
        />
    );
};

export default AutoCompleteAddressInput;
